import React, { useState, useCallback, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Header,
  SpaceBetween,
  ContentLayout,
} from "@cloudscape-design/components";
import { ROUTES } from "../../constants";
import { ConfigurableForm } from "../common/Forms";
import { mfrFormSchema } from "./schemas/mfr-form-schema";
import { createLeafSection } from "../../utils/documentSectionHelpers";
import { useFormStore } from "../../store/form-stores";
import { SCALE_CONFIG } from "../../constants/scaleConfig";
import { useGlobalStore } from "../../store/global-store";
import { useWorkflowStore } from "../../store/workflow-store";
import { useNotificationStore } from "../../store/notification-store";
import { useProductLineStaticFilter } from "../../hooks/useProductLineStaticFilter";
import { useSessionFormId } from "../../hooks/useSessionFormId";
import { FIELD_EVENTS, SEARCH_CONFIG } from "../../constants/StaticFilterValues";
import { LoadingScreen } from "../common/UIStates";
import { ConfirmationModal } from "../common/Modals";
import { PL_SLICE_OPTIONS, PL_DRILL_DOWN_OPTIONS } from "../../config/mfr-workflow-config";
import {
  ACTUALS_VALUE,
  USD_CONSTANT_VALUE,
  MILLIONS_VALUE,
  USD_CONSTANT_LABEL, ACTUALS_LABEL,
  PNL_DRILL_DOWN_REPORTING_LINE_MAP
} from "../../constants/mfr-constants";
import { WORKFLOW_TAGS } from "../../constants/workflow-tags";
import { getCapabilityId } from '../../utils/capabilityMapping';
import { getComparisonScenarioOptions } from "../../utils/comparisonScenarioUtils";
import { findCurrentPeriodValue } from "../../utils/PeriodGenerator";
import { getLabelByValue, getOptionByValue } from '../../utils/optionUtils';
import { expandMarketplaceGroups } from '../../utils/marketplaceUtils';
import {WorkflowTemplate} from "@amzn/finspace-service-typescript-client";
import { DocumentSectionsTableField } from "../../types/formSchema";
import { logger } from '../../utils/logger';

export const MFRCreatePage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditMode = Boolean(id);
  
  // Generate session-specific form ID for this tab
  const [sessionFormId, cleanupSession] = useSessionFormId(mfrFormSchema.id, isEditMode, id);
  
  const { setFormData, getFormData } = useFormStore();
  const { selectedSpaceId, ensureSpaceSelected, fetchSpaces } = useGlobalStore();
  const { workflows, workflowTemplates, fetchWorkflows, fetchWorkflowTemplates, createWorkflow, updateWorkflow } = useWorkflowStore();
  const { error: showError, success: showSuccess } = useNotificationStore();
  
  const [isLoadingWorkflow, setIsLoadingWorkflow] = useState(isEditMode);
  const [documentSectionError, setDocumentSectionError] = useState<string>('');
  const [cancelModal, setCancelModal] = useState<boolean>(false);
  
  // Fetch spaces and workflow templates on mount
  useEffect(() => {
    fetchSpaces();
  }, [fetchSpaces]);

  useEffect(() => {
    if (selectedSpaceId) {
      fetchWorkflowTemplates();
      if (isEditMode) {
        fetchWorkflows(selectedSpaceId);
      }
    }
  }, [selectedSpaceId, fetchWorkflowTemplates, fetchWorkflows, isEditMode]);

  // Load workflow data for edit mode
  useEffect(() => {
    const loadWorkflowData = async () => {
      if (isEditMode && id && workflows.length > 0) {
        const workflow = workflows.find(w => w.id === id);
        if (workflow) {
          await populateFormFromWorkflow(workflow);
          setIsLoadingWorkflow(false);
        } else {
          setIsLoadingWorkflow(false);
        }
      }
    };
    
    loadWorkflowData();
  }, [isEditMode, id, workflows]);

  // Helper function to check if a value exists in treeData
  const isValueInTreeData = (treeData: any[], value: string): boolean => {
    for (const node of treeData) {
      if (node.value === value || node.key === value) {
        return true;
      }
      if (node.children && node.children.length > 0) {
        if (isValueInTreeData(node.children, value)) {
          return true;
        }
      }
    }
    return false;
  };

  // Function to populate form from workflow data
  const populateFormFromWorkflow = async (workflow: any) => {
    const transformedInput = transformFromArrays(workflow.input || {});
    
    // Get period tree data from schema
    const periodField = mfrFormSchema.sections.flatMap(s => s.fields).find(f => f.name === 'period') as any;
    const periodTreeData = periodField?.treeData || [];
    
    // Map period value to current tree value (handles QTD/YTD → complete format conversion)
    const currentPeriodValue = transformedInput.period 
      ? findCurrentPeriodValue(periodTreeData, transformedInput.period)
      : transformedInput.period;
    
    const formData: any = {
      name: workflow.name || '',
      description: workflow.description || '',
      marketplace: transformedInput.marketplace,
      channel: transformedInput.channel,
      product_lines: transformedInput.product_lines,
      period: currentPeriodValue,
      base_scenario: transformedInput.base_scenario || ACTUALS_VALUE,
      comparison_scenario: transformedInput.comparison_scenario,
      currency: transformedInput.currency || USD_CONSTANT_VALUE,
      data_source: transformedInput.data_source,
    };

    // Populate document sections from workflow steps
    const documentSections: any = {};
    const plSliceFilters: string[] = [];
    const plDrillDownFilters: string[] = [];
    const plSliceChildren: any[] = [];
    const plDrillDownChildren: any[] = [];

    if (workflow.steps) {
      workflow.steps.forEach((step: any) => {
        documentSections[step.id] = {
          includeTable: step.input?.include_table_generation || false,
          includeCommentary: step.input?.include_commentary_generation || false,
          scale: step.input?.scale || MILLIONS_VALUE
        };

        // Extract P&L By GL filter from the 'executive_pnl_pnl_by_gl' step
        if (step.id === 'executive_pnl_pnl_by_gl') {
          documentSections['executive_pnl_pnl_by_gl'] = {
            includeTable: step.input?.include_table_generation || false,
            includeCommentary: step.input?.include_commentary_generation || false,
            scale: step.input?.scale || MILLIONS_VALUE,
            ...(step.input?.product_lines && { filterValue: step.input.product_lines })
          };
        }

        // Check if this step belongs to dynamic sections

        const plDrillDownOptions = PL_DRILL_DOWN_OPTIONS;

        // Check if step is a P&L Slice dynamic section
        const sliceOption = getOptionByValue(PL_SLICE_OPTIONS, step.id);
        if (sliceOption) {
          plSliceFilters.push(step.id);
          plSliceChildren.push(createLeafSection(step.id, sliceOption.label, {
            includeTable: step.input?.include_table_generation ?? false,
            includeCommentary: step.input?.include_commentary_generation ?? false,
            scale: step.input?.scale || MILLIONS_VALUE
          }));
        }

        // Check if step is a P&L Drill Down dynamic section
        const drillDownOption = getOptionByValue(plDrillDownOptions, step.id);
        if (drillDownOption) {
          plDrillDownFilters.push(step.id);
          plDrillDownChildren.push(createLeafSection(step.id, drillDownOption.label, {
            includeTable: step.input?.include_table_generation ?? false,
            includeCommentary: step.input?.include_commentary_generation ?? false,
            scale: step.input?.scale || MILLIONS_VALUE
          }));
        }
      });
    }

    // Set filter values in documentSections for the table component
    if (plSliceFilters.length > 0) {
      documentSections['pl-slice'] = { filterValue: plSliceFilters };
    }
    if (plDrillDownFilters.length > 0) {
      documentSections['pl-drill-down'] = { filterValue: plDrillDownFilters };
    }

    formData.documentSections = documentSections;

    // Set form data
    setFormData(sessionFormId, formData);
    
    // Check if product_lines value exists in static treeData
    if (formData.product_lines) {
      const product_linesField = mfrFormSchema.sections[1].fields.find(f => f.name === 'product_lines') as any;
      const staticTreeData = product_linesField?.treeData || [];
      
      // If the value is not in the static tree data, fetch it from the API
      if (!isValueInTreeData(staticTreeData, formData.product_lines)) {
        try {
          // Reuse existing loadFilterData to search for the product line
          const response = await loadFilterData(
            'productid',
            { key: formData.product_lines },
            undefined
          );
          
          if (response && response.children && response.children.length > 0) {
            // Use helper to process nodes and pre-load children for non-leaf nodes
            const processedResults = await processNodesWithChildren(
              response.children,
              'productid'
            );
            
            // Find the matching node in the processed results
            const matchingNode = processedResults.find((node: any) => 
              node.value === formData.product_lines || node.key === formData.product_lines
            );
            
            if (matchingNode) {
              // Store in both dynamicOptions and cache for GL dropdown to use
              setAllLoadedNodes(prev => {
                const newMap = new Map(prev);
                newMap.set(matchingNode.value || matchingNode.key, matchingNode);
                return newMap;
              });
              
              // Add the fetched node to dynamic options so TreeSelect can display it with label
              setDynamicOptions(prev => ({
                ...prev,
                'product_lines': [matchingNode, ...staticTreeData]
              }));
              
              // Build GL dropdown options from children immediately (don't wait for loadGLOptionsForProductLine)
              // This ensures the full dropdown is populated before the form tries to display selected values
              buildGLOptionsFromNode(matchingNode);
            } else if (processedResults.length > 0) {
              const firstNode = processedResults[0];
              
              setAllLoadedNodes(prev => {
                const newMap = new Map(prev);
                newMap.set(firstNode.value || firstNode.key, firstNode);
                return newMap;
              });
              
              setDynamicOptions(prev => ({
                ...prev,
                'product_lines': [firstNode, ...staticTreeData]
              }));
              
              buildGLOptionsFromNode(firstNode);
            } else {
              loadGLOptionsForProductLine(formData.product_lines);
            }
          }
        } catch (error) {
          logger.error('Failed to fetch product line node data:', error as Error);
          // Fall back to loading GL options from static tree data
          loadGLOptionsForProductLine(formData.product_lines);
        }
      } else {
        // Product line is in static tree data, still need to load GL options
        loadGLOptionsForProductLine(formData.product_lines);
      }
    }
    
    // Set dynamic options for children
    const comparisonOptions = getComparisonScenarioOptions(formData.period);
    const hasPeriod = formData.period && formData.period !== '';
    setDynamicOptions(prev => ({
      ...prev,
      ...(plSliceChildren.length > 0 && { 'documentSections_pl-slice_children': plSliceChildren }),
      ...(plDrillDownChildren.length > 0 && { 'documentSections_pl-drill-down_children': plDrillDownChildren }),
      comparison_scenario: comparisonOptions,
      comparison_scenario_disabled: !hasPeriod,
      comparison_scenario_placeholder: hasPeriod ? 'Select comparison scenario...' : 'Select Period First'
    }));
    logger.info('Populated form data from workflow:', { formData });
  };
  
  // State for dynamic dropdown options - initialize with default period if available
  const [dynamicOptions, setDynamicOptions] = useState<Record<string, any>>(() => {
    const defaultPeriod = mfrFormSchema.sections
      .flatMap(s => s.fields)
      .find(f => f.name === 'period')?.defaultValue;
    
    const hasPeriod = defaultPeriod && defaultPeriod !== '';
    return {
      comparison_scenario: getComparisonScenarioOptions(defaultPeriod || ''),
      comparison_scenario_disabled: !hasPeriod,
      comparison_scenario_placeholder: hasPeriod ? 'Select comparison scenario...' : 'Select Period First'
    };
  });

  // Transform single values to arrays for consistent API format
  const transformToArrays = (formData: any) => {
    return {
      ...formData,
      marketplace: Array.isArray(formData.marketplace) ? formData.marketplace : [formData.marketplace].filter(Boolean),
      period: Array.isArray(formData.period) ? formData.period : [formData.period].filter(Boolean),
      channel: Array.isArray(formData.channel) ? formData.channel : [formData.channel].filter(Boolean),
      product_lines: Array.isArray(formData.product_lines) ? formData.product_lines : [formData.product_lines].filter(Boolean),
      base_scenario: Array.isArray(formData.base_scenario) ? formData.base_scenario : [formData.base_scenario].filter(Boolean),
      currency: Array.isArray(formData.currency) ? formData.currency : [formData.currency].filter(Boolean),
      data_source: Array.isArray(formData.data_source) ? formData.data_source : [formData.data_source].filter(Boolean),
      // Keep already-array fields as is
      comparison_scenario: formData.comparison_scenario,
    };
  };

  // Transform arrays back to single values for UI (for single-select fields)
  const transformFromArrays = (workflowInput: any) => {
    // Helper to extract value from object or return as-is
    const extractValue = (item: any) => {
      if (typeof item === 'object' && item !== null && 'value' in item) {
        return item.value;
      }
      return item;
    };
    
    return {
      ...workflowInput,
      // Keep multiple-select fields as arrays (extract values from objects)
      marketplace: (workflowInput.marketplace || []).map(extractValue),
      comparison_scenario: (workflowInput.comparison_scenario || []).map(extractValue),
      channel: (workflowInput.channel || []).map(extractValue),
      // Convert single-select fields from arrays to single values (extract from objects)
      period: Array.isArray(workflowInput.period) 
        ? extractValue(workflowInput.period[0]) 
        : extractValue(workflowInput.period) || '',
      product_lines: Array.isArray(workflowInput.product_lines) 
        ? extractValue(workflowInput.product_lines[0]) 
        : extractValue(workflowInput.product_lines) || '',
      base_scenario: Array.isArray(workflowInput.base_scenario) 
        ? extractValue(workflowInput.base_scenario[0]) 
        : extractValue(workflowInput.base_scenario) || '',
      currency: Array.isArray(workflowInput.currency) 
        ? extractValue(workflowInput.currency[0]) 
        : extractValue(workflowInput.currency) || '',
      data_source: Array.isArray(workflowInput.data_source) 
        ? extractValue(workflowInput.data_source[0]) 
        : extractValue(workflowInput.data_source),
    };
  };
  
  // State to track all nodes that have been loaded/expanded for GL options
  const [allLoadedNodes, setAllLoadedNodes] = useState<Map<string, any>>(new Map());
  
  // Track which specific node is loading
  const [loadingNodes, setLoadingNodes] = useState<Set<string>>(new Set());
  
  // Track GL options loading state
  const [isGLOptionsLoading, setIsGLOptionsLoading] = useState<boolean>(false);
  
  // Search state for ProductLine
  const [isSearchLoading, setIsSearchLoading] = useState(false);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  // Initialize filter hooks
  const { loadFilterData } = useProductLineStaticFilter((error: Error) => {
    logger.error('Filter loading error:', error as Error);
  });

  /**
   * Helper function to process nodes and pre-load children for non-leaf nodes
   * Used by both search and edit mode to ensure consistent behavior
   */
  const processNodesWithChildren = useCallback(async (
    nodes: any[], 
    filterType: string,
    signal?: AbortSignal
  ): Promise<any[]> => {
    return Promise.all(
      nodes.map(async (node: any) => {
        if (node.isLeaf === false) {
          try {
            const nodeWithLevel = {
              ...node,
              level: node.level
            };
            
            const childrenResponse = await loadFilterData(
              filterType,
              nodeWithLevel,
              undefined,
              signal
            );
            
            if (childrenResponse && childrenResponse.children) {
              return {
                ...node,
                children: childrenResponse.children,
                expanded: true
              };
            }
          } catch (error) {
            if ((error as Error).name === 'AbortError') {
              throw error;
            }
            logger.error('Error loading children for node', error as Error);
          }
        }
        
        return node;
      })
    );
  }, [loadFilterData]);

  /**
   * Helper function to build GL dropdown options from a product line node
   * Handles both nodes with children and leaf nodes
   */
  const buildGLOptionsFromNode = useCallback((node: any) => {
    if (node.children && node.children.length > 0) {
      const glOptions = node.children.map((child: any) => ({
        value: child.value,
        label: child.title
      }));
      
      setDynamicOptions(prev => ({ 
        ...prev, 
        'documentSections_executive_pnl_pnl_by_gl': glOptions 
      }));
    } else if (node.isLeaf === true) {
      const glOptions = [{
        value: node.value,
        label: node.title
      }];
      
      setDynamicOptions(prev => ({ 
        ...prev, 
        'documentSections_executive_pnl_pnl_by_gl': glOptions 
      }));
    } else {
      logger.error('Product line node has no children:', node);
      setDynamicOptions(prev => ({ 
        ...prev, 
        'documentSections_executive_pnl_pnl_by_gl': [] 
      }));
    }
  }, []);

  // Cleanup form data on component unmount
  useEffect(() => {
    return () => {
      setFormData(sessionFormId, {});
      cleanupSession();
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [setFormData, sessionFormId, cleanupSession]);

  // Handle cancellation
  const handleCancel = () => {
    setCancelModal(true);
  };
  
  const handleCancelConfirm = () => {
    // Clear all form data from store
    setFormData(sessionFormId, {});
    cleanupSession();
    navigate(ROUTES.MFR_VIEW);
  };
  
  const handleCancelModalClose = () => {
    setCancelModal(false);
  };


  // Helper function to get capability ID for a section
  const getCapabilityIdForSection = (workflowTemplates: WorkflowTemplate[], sectionId: string) => {
    const mfrTemplate = workflowTemplates.find(template => template.tags?.includes(WORKFLOW_TAGS.MFR.value));
    return mfrTemplate ? getCapabilityId(mfrTemplate, sectionId) : undefined;
  };

  // Helper function to get slice filters for P&L by Slice sections
  const getSliceFilters = (id: string) => {
    if (id.startsWith('pnl_by_slice_')) {
      const sliceName = id.replace('pnl_by_slice_', 'slice_');
      return { slice_filters: { slice_name: sliceName } };
    }
    return {};
  };

  // Helper function to get reporting line for P&L Drill Down sections
  const getDrillDownFilters = (id: string) => {
    const reportingLine = PNL_DRILL_DOWN_REPORTING_LINE_MAP[id];
    if (reportingLine) {
      return { reporting_line: [reportingLine] };
    }
    return {};
  };

  // Function to get step name with proper mapping and prefixes
  const getStepName = (stepId: string): string => {
    // Regular section mapping
    const sectionNames: Record<string, string> = {
      'executive_pnl_executive_summary': 'Executive Summary',
      'executive_pnl_pnl_by_channel': 'P&L by Channel',
      'executive_pnl_pnl_by_gl': 'P&L by GL',
      'executive_pnl_pnl_by_marketplace': 'P&L by Marketplace',
      'business_trends_top_bottom_brands': 'Top and Bottom 10 Brands',
      'business_trends_top_bottom_asins': 'Top and Bottom 10 ASINs',
      'cost_to_serve': 'Cost to Serve',
      'detailed_pnl': 'Detailed P&L'
    };

    // Check if it's a regular section
    if (sectionNames[stepId]) {
      return sectionNames[stepId];
    }

    // Check if it's a P&L Slice dynamic section
    const sliceOption = getOptionByValue(PL_SLICE_OPTIONS, stepId);
    if (sliceOption) {
      return `P&L by Slice - ${sliceOption.label}`;
    }

    // Check if it's a P&L Drill Down dynamic section
    const drillDownOption = getOptionByValue(PL_DRILL_DOWN_OPTIONS, stepId);
    if (drillDownOption) {
      return `P&L Drill Down - ${drillDownOption.label}`;
    }

    // Fallback to stepId
    return stepId;
  };

  // Extract section order from schema
  const getSchemaOrder = (): Array<{type: 'static' | 'dynamic', id?: string, filterKey?: string, filterOptions?: Array<{value: string, label: string}>}> => {
    const order: Array<{type: 'static' | 'dynamic', id?: string, filterKey?: string, filterOptions?: Array<{value: string, label: string}>}> = [];
    const docSection = mfrFormSchema.sections.find(s => s.fields.some(f => f.type === 'documentSectionsTable'));
    const tableField = docSection?.fields.find(f => f.type === 'documentSectionsTable') as DocumentSectionsTableField | undefined;
    
    tableField?.tableData?.forEach((item: any) => {
      if (item.filterOptions) {
        // Dynamic section placeholder - include filterOptions for ordering
        order.push({type: 'dynamic', filterKey: `documentSections_${item.id}_children`, filterOptions: item.filterOptions});
      } else if (item.children) {
        // Static children sections
        item.children.forEach((child: any) => 
          order.push({type: 'static', id: child.id})
        );
      } else if (item.id) {
        // Standalone static section
        order.push({type: 'static', id: item.id});
      }
    });
    
    return order;
  };

  // Build a workflow step from section configuration
  // Build a workflow step from section configuration
  const buildStepForSection = (sectionId: string, sectionData: any, documentSections: Record<string, any>, formData?: any) => {
    const allDynamicChildren = [
      ...(dynamicOptions['documentSections_pl-slice_children'] || []),
      ...(dynamicOptions['documentSections_pl-drill-down_children'] || [])
    ];
    const dynamicChild = allDynamicChildren.find(child => child.id === sectionId);
    
    const stepInput: any = {
      include_table_generation: sectionData.includeTable !== undefined ? sectionData.includeTable : (dynamicChild?.includeTable || false),
      include_commentary_generation: sectionData.includeCommentary !== undefined ? sectionData.includeCommentary : (dynamicChild?.includeCommentary || false),
      scale: sectionData.scale || dynamicChild?.scale || MILLIONS_VALUE,
      ...getSliceFilters(sectionId),
      ...getDrillDownFilters(sectionId)
    };

    // Add product_line_filter for P&L by GL step if GL filter is selected
    if (sectionId === 'executive_pnl_pnl_by_gl' && documentSections['executive_pnl_pnl_by_gl']?.filterValue) {
      stepInput.product_lines = documentSections['executive_pnl_pnl_by_gl'].filterValue;
    }

    // Add marketplace filter for P&L by Marketplace step - expand groups to individual marketplaces
    if (sectionId === 'executive_pnl_pnl_by_marketplace' && formData?.marketplace) {
      const marketplaceField = mfrFormSchema.sections
        .find(s => s.title === 'Filters')
        ?.fields.find(f => f.name === 'marketplace') as any;
      const marketplaceTreeData = marketplaceField?.treeData || [];
      
      // Get marketplace values from form data
      const marketplaceValues = Array.isArray(formData.marketplace) 
        ? formData.marketplace 
        : [formData.marketplace].filter(Boolean);
      
      // Expand groups (EU5, LATAM, MENA) to individual marketplaces
      const expandedMarketplaces = expandMarketplaceGroups(marketplaceValues, marketplaceTreeData);
      stepInput.marketplace = expandedMarketplaces;
    }

    // Add special input for Business Trends sections
    if (sectionId === 'business_trends_top_bottom_brands') {
      stepInput.filterCriteria = 'brands';
    } else if (sectionId === 'business_trends_top_bottom_asins') {
      stepInput.filterCriteria = 'ASIN';
    }

    return {
      id: sectionId,
      name: getStepName(sectionId),
      capabilityId: getCapabilityIdForSection(workflowTemplates, sectionId),
      input: stepInput
    };
  };

  // Handle Save action
  const handleSave = async (data: any) => {
    logger.info("MFR form saved:", {
      formData: data,
      timestamp: new Date().toISOString(),
    });

    try {
      const spaceId = await ensureSpaceSelected();
      // Build steps from document sections in schema order
      let steps: Array<{id: string, name: string, capabilityId?: string, input: Record<string, any>}> = [];
      const documentSections = data.documentSections || {};
      
      // Process all sections from documentSections (includes user modifications)
      Object.entries(documentSections).forEach(([sectionId, sectionData]: [string, any]) => {
        // Skip parent sections - they only store filter selections, not actual steps
        const isParentSection = sectionId === 'pl-slice' || sectionId === 'pl-drill-down';
        if (isParentSection) {
          return;
        }
        
        // For dynamic children, verify they're still selected in the parent dropdown
        const isDynamicChild = PL_SLICE_OPTIONS.some(opt => opt.value === sectionId) || 
                               PL_DRILL_DOWN_OPTIONS.some(opt => opt.value === sectionId);
        
        if (isDynamicChild) {
          // Determine which parent this child belongs to
          const isSliceChild = PL_SLICE_OPTIONS.some(opt => opt.value === sectionId);
          const parentId = isSliceChild ? 'pl-slice' : 'pl-drill-down';
          const selectedFilters = documentSections[parentId]?.filterValue || [];
          
          // Only include if this child is in the parent's selected filters
          if (!selectedFilters.includes(sectionId)) {
            return;
          }
        }
        
        // Only include sections that have actual configuration (at least one checkbox checked)
        if (sectionData.includeTable || sectionData.includeCommentary) {
          steps.push(buildStepForSection(sectionId, sectionData, documentSections, data));
        }
      });
      
      // Reorder steps according to schema order
      const schemaOrder = getSchemaOrder();
      const orderedSteps: typeof steps = [];
      const processedStepIds = new Set<string>();
      
      schemaOrder.forEach(entry => {
        if (entry.type === 'static') {
          const step = steps.find(s => s.id === entry.id && !processedStepIds.has(s.id));
          if (step) {
            orderedSteps.push(step);
            processedStepIds.add(step.id);
          }
        } else if (entry.type === 'dynamic') {
          // For dynamic sections, iterate through schema filterOptions to preserve dropdown order
          const filterOptions = entry.filterOptions || [];
          
          filterOptions.forEach(option => {
            const step = steps.find(s => s.id === option.value && !processedStepIds.has(s.id));
            if (step) {
              orderedSteps.push(step);
              processedStepIds.add(step.id);
            }
          });
        }
      });
      
      steps = orderedSteps;

      // Validate that at least one step is selected
      if (steps.length === 0) {
        setDocumentSectionError('Report section required: Please select at least one section.');
        return;
      }
      
      // Clear error if validation passes
      setDocumentSectionError('');

      // Helper functions to get field labels and objects
      const getFieldLabels = (fieldName: string, values: string[]) => {
        const filtersSection = mfrFormSchema.sections.find(section => section.title === 'Filters');
        const field = filtersSection?.fields.find(f => f.name === fieldName) as any;
        
        // Try dynamic options first (for dynamically populated fields)
        const dynamicFieldOptions = dynamicOptions[fieldName];
        if (dynamicFieldOptions && Array.isArray(dynamicFieldOptions)) {
          // For tree structures, build lookup from dynamic options
          if (field?.type === 'treeSelect' || field?.type === 'tree') {
            const buildLookupMap = (nodes: any[]): Record<string, string> => {
              const map: Record<string, string> = {};
              const processNode = (node: any): void => {
                map[node.value] = node.title;
                if (node.children?.length) {
                  node.children.forEach(processNode);
                }
              };
              nodes.forEach(processNode);
              return map;
            };
            const labelMap = buildLookupMap(dynamicFieldOptions);
            return values.map(v => labelMap[v] || v);
          }
          // For regular dropdowns
          return values.map(v => getLabelByValue(dynamicFieldOptions, v));
        }
        
        // Try static field options (for fields with predefined options)
        if (field?.options) {
          return values.map(v => getLabelByValue(field.options, v));
        }
        
        // For tree fields, build lookup map for O(1) access
        const treeData = field?.treeData || [];
        const buildLookupMap = (nodes: any[]): Record<string, string> => {
          const map: Record<string, string> = {};
          const processNode = (node: any): void => {
            map[node.value] = node.title;
            if (node.children?.length) {
              node.children.forEach(processNode);
            }
          };
          nodes.forEach(processNode);
          return map;
        };
        const labelMap = buildLookupMap(treeData);
        return values.map(v => labelMap[v] || v);
      };

      const getFieldObjects = (fieldName: string, values: string[]) => {
        const labels = getFieldLabels(fieldName, values);
        return values.map((value, index) => ({ label: labels[index], value }));
      };

      // Transform form data to objects with both label and value
      const arrayValues = transformToArrays({
        product_lines: data.product_lines,
        marketplace: data.marketplace,
        currency: data.currency,
        period: data.period,
        base_scenario: data.base_scenario,
        comparison_scenario: data.comparison_scenario,
        channel: data.channel,
        data_source: data.data_source
      });

      const transformedInput = {
        product_lines: getFieldObjects('product_lines', arrayValues.product_lines),
        marketplace: getFieldObjects('marketplace', arrayValues.marketplace),
        currency: getFieldObjects('currency', arrayValues.currency),
        period: getFieldObjects('period', arrayValues.period),
        base_scenario: [{label: ACTUALS_LABEL, value: ACTUALS_VALUE}],
        comparison_scenario: getFieldObjects('comparison_scenario', arrayValues.comparison_scenario),
        channel: getFieldObjects('channel', arrayValues.channel),
        data_source: getFieldObjects('data_source', arrayValues.data_source)
      };

      // Get reports from MFR template
      const mfrTemplate = workflowTemplates.find(template => template.tags?.includes(WORKFLOW_TAGS.MFR.value));
      const reports = mfrTemplate?.reports || [];

      // Build CreateWorkflowRequest from form data
      const workflowRequest = {
        name: data.name,
        description: data.description,
        input: transformedInput,
        steps: steps,
        reports: reports,
        tags: [WORKFLOW_TAGS.MFR.value]
      };

      // Call the appropriate API based on edit mode
      let response;
      if (isEditMode && id) {
        response = await updateWorkflow(spaceId, id, { 
          spaceId, 
          workflowId: id, 
          workflow: workflowRequest 
        });
      } else {
        response = await createWorkflow({ spaceId, workflow: workflowRequest });
      }
      
      logger.info(`Workflow ${isEditMode ? 'updated' : 'created'} successfully:`, { response });
      
      // Show success notification
      showSuccess(`MFR Report "${data.name || "Untitled"}" ${isEditMode ? 'updated' : 'saved'} successfully!`, "Success");
      
      // Clear form data and navigate
      setFormData(sessionFormId, {});
      cleanupSession();
      navigate(ROUTES.MFR_VIEW);
      
    } catch (error: any) {
      logger.error("Failed to save MFR report:", error as Error);
      showError(`Failed to ${isEditMode ? 'update' : 'save'} MFR Report: ${error?.message || 'Unknown error'}`);
    }
  };

  /**
   * Handles ProductLine search functionality with debouncing
   */
  const handleProductLineSearch = useCallback(async (searchValue: string, filterType: string) => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Show loading immediately when typing 3+ characters
    if (searchValue && searchValue.trim().length >= SEARCH_CONFIG.MIN_SEARCH_LENGTH) {
      setIsSearchLoading(true);
    }

    searchTimeoutRef.current = setTimeout(async () => {
      if (!searchValue || searchValue.trim().length < SEARCH_CONFIG.MIN_SEARCH_LENGTH) {
        setIsSearchLoading(false);
        
        // Reset to default tree data
        const product_linesField = mfrFormSchema.sections[1].fields.find(f => f.name === 'product_lines') as any;
        const defaultTreeData = product_linesField?.treeData || [];
        
        setDynamicOptions(prev => ({
          ...prev,
          product_lines: defaultTreeData
        }));
        
        return;
      }
      
      try {
        abortControllerRef.current = new AbortController();
        
        // Clear current tree data
        setDynamicOptions(prev => ({
          ...prev,
          product_lines: []
        }));

        const response = await loadFilterData(
          filterType,
          { key: searchValue },
          undefined,
          abortControllerRef.current.signal
        );
        
        if (response && response.children) {
          // Use helper to process nodes and pre-load children for non-leaf nodes
          const processedResults = await processNodesWithChildren(
            response.children,
            filterType,
            abortControllerRef.current?.signal
          );
          
          setDynamicOptions(prev => ({
            ...prev,
            product_lines: processedResults
          }));
        } else {
          setDynamicOptions(prev => ({
            ...prev,
            product_lines: []
          }));
        }
        
      } catch (error) {
        if ((error as Error).name === 'AbortError') {
          return;
        }
        logger.error('Error searching product lines', error as Error);
        setDynamicOptions(prev => ({
          ...prev,
          product_lines: []
        }));
      } finally {
        setIsSearchLoading(false);
      }
    }, SEARCH_CONFIG.DEBOUNCE_DELAY);
  }, [loadFilterData, mfrFormSchema.sections, dynamicOptions.product_lines]);

  /**
   * Handles ProductLine dynamic node loading
   */
  const handleProductLineLoad = useCallback(async (node: any, filterType: string) => {
    try {
      // Skip if node already has children loaded
      if (node.children && node.children.length > 0) {
        return;
      }
      
      // Track this specific node as loading
      const newLoadingNodes = new Set(loadingNodes);
      newLoadingNodes.add(node.key || node.value);
      setLoadingNodes(newLoadingNodes);
      
      const data = await loadFilterData(filterType, node, node.hierarchyType);
      
      if (data && data.children) {
        // Store this node and its children in our tracking map
        setAllLoadedNodes(prev => {
          const newMap = new Map(prev);
          newMap.set(node.key || node.value, {
            ...node,
            children: data.children
          });
          return newMap;
        });
        
        // Get current tree data from schema or dynamic options
        const product_linesField = mfrFormSchema.sections[1].fields.find(f => f.name === 'product_lines') as any;
        const currentTreeData = dynamicOptions.product_lines || product_linesField?.treeData || [];
        
        // Update the tree data by adding children to the expanded node
        const updatedTreeData = updateTreeNodeChildren(currentTreeData, node, data.children);
        
        // Remove the children from the flat list to avoid duplicates
        // (search results contain both parent and children as separate nodes)
        const childValues = data.children.map((child: any) => child.value || child.key);
        
        const deduplicatedTreeData = updatedTreeData.filter((treeNode: any) => {
          const nodeValue = treeNode.value || treeNode.key;
          const isChild = childValues.includes(nodeValue);
          const isParent = (treeNode.key === node.key || treeNode.value === node.value);
          
          // Keep the node if it's not one of the children we just added as nested, OR if it's the parent
          return !isChild || isParent;
        });
        
        setDynamicOptions(prev => ({
          ...prev,
          product_lines: deduplicatedTreeData
        }));
      }
    } catch (error) {
      logger.error('Failed to load filter data:', error as Error);
    } finally {
      // Remove this node from loading set
      const newLoadingNodes = new Set(loadingNodes);
      newLoadingNodes.delete(node.key || node.value);
      setLoadingNodes(newLoadingNodes);
    }
  }, [loadFilterData, loadingNodes, mfrFormSchema.sections, dynamicOptions.product_lines]);

  /**
   * Loads GL options for the selected product line by fetching its immediate children
   */
  const loadGLOptionsForProductLine = useCallback(async (selectedValue: string) => {
    setIsGLOptionsLoading(true);
    
    try {
      // First check if we have this node in our loaded nodes map
      let selectedNode = allLoadedNodes.get(selectedValue);
      
      if (!selectedNode) {
        // If not in loaded nodes, search in current tree data
        const product_linesField = mfrFormSchema.sections[1].fields.find(f => f.name === 'product_lines') as any;
        const currentTreeData = dynamicOptions.product_lines || product_linesField?.treeData || [];
        
        const findNodeInTree = (nodes: any[], value: string): any => {
          for (const node of nodes) {
            if (node.value === value || node.key === value) {
              return node;
            }
            if (node.children && node.children.length > 0) {
              const found = findNodeInTree(node.children, value);
              if (found) return found;
            }
          }
          return null;
        };
        
        selectedNode = findNodeInTree(currentTreeData, selectedValue);
      }
      
      if (!selectedNode) {
        logger.warn('Selected product line node not found:', { selectedValue });
        // For search-selected nodes, create a minimal node structure to fetch children
        selectedNode = {
          value: selectedValue,
          key: selectedValue,
          level: 0, // Will be determined by API
          hierarchyType: null // Will be determined by node value
        };
      }
      
      // Check if this is a leaf node (no children expected)
      if (selectedNode.isLeaf === true) {
        // Show the parent node itself as option
        const glOptions = [{
          value: selectedNode.value,
          label: selectedNode.title
        }];
        
        setDynamicOptions(prev => ({ 
          ...prev, 
          'documentSections_executive_pnl_pnl_by_gl': glOptions 
        }));
        return;
      }
      
      // Check if we already have children for this node
      if (selectedNode.children && selectedNode.children.length > 0) {
        const glOptions = selectedNode.children.map((child: any) => ({
          value: child.value,
          label: child.title
        }));
        
        setDynamicOptions(prev => ({ 
          ...prev, 
          'documentSections_executive_pnl_pnl_by_gl': glOptions 
        }));
        return;
      }
      
      // For GL options, we need to get children
      // The API expects the level of children we want, not the parent level
      let childLevel;
      if (selectedNode.level !== undefined) {
        childLevel = selectedNode.level + 1;
      } else {
        // Root node - children are at level 1  
        childLevel = 1;
      }
      
      // But if this node was loaded from expansion, it might not have the correct level
      // Let's also try to determine level from the tree position
      const determineNodeLevel = (nodes: any[], targetValue: string, currentLevel = 0): number => {
        for (const node of nodes) {
          if (node.value === targetValue) {
            return currentLevel;
          }
          if (node.children && node.children.length > 0) {
            const found = determineNodeLevel(node.children, targetValue, currentLevel + 1);
            if (found !== -1) return found;
          }
        }
        return -1;
      };
      
      const product_linesField = mfrFormSchema.sections[1].fields.find(f => f.name === 'product_lines') as any;
      const currentTreeData = dynamicOptions.product_lines || product_linesField?.treeData || [];
      const actualLevel = determineNodeLevel(currentTreeData, selectedValue);
      
      // Determine if this is a search-selected node vs tree-navigated node
      // Search results replace the entire tree data with flat search results
      // Tree navigation preserves the original hierarchical structure
      const originalTreeData = (mfrFormSchema.sections[1].fields.find(f => f.name === 'product_lines') as any)?.treeData || [];
      const isOriginalTreeStructure = currentTreeData.length === originalTreeData.length && 
                                     currentTreeData.every((node: any, index: number) => 
                                       originalTreeData[index] && node.value === originalTreeData[index].value);
      
      const isSearchSelected = !isOriginalTreeStructure;
      
      // Determine hierarchy type if not set
      let hierarchyName = selectedNode.hierarchyType;
      
      if (!hierarchyName) {
        if (isSearchSelected) {
          // For search results, pass undefined hierarchy
          // Let the API search across all hierarchies to find the node
          hierarchyName = undefined;
        } else {
          // For tree navigation, determine from root node value
          if (['A1PTOT', 'PTOT', 'altProduct'].includes(selectedNode.value)) {
            const hierarchyMap: Record<string, string> = {
              'A1PTOT': 'COGNOS_PRODUCT_ALTERNATE_HIERARCHY',
              'PTOT': 'COGNOS_PRODUCT_HIERARCHY',
              'altProduct': 'COGNOS_PRODUCT_ALTERNATE_ALT_HIERARCHY'
            };
            hierarchyName = hierarchyMap[selectedNode.value];
          } else {
            hierarchyName = 'COGNOS_PRODUCT_HIERARCHY';
          }
        }
      }
      
      // Calculate the correct level based on selection type
      let targetLevel;
      if (isSearchSelected) {
        // For search results, use the same level
        targetLevel = selectedNode.level;
      } else {
        // For tree navigation, use level + 1 to get children
        // But if level is undefined, try to determine it from the tree structure
        if (selectedNode.level !== undefined) {
          targetLevel = selectedNode.level + 1;
        } else {
          // Try to determine level from tree position
          const determineNodeLevel = (nodes: any[], targetValue: string, currentLevel = 0): number => {
            for (const node of nodes) {
              if (node.value === targetValue) {
                return currentLevel;
              }
              if (node.children && node.children.length > 0) {
                const found = determineNodeLevel(node.children, targetValue, currentLevel + 1);
                if (found !== -1) return found;
              }
            }
            return -1;
          };
          
          const nodeLevel = determineNodeLevel(currentTreeData, selectedValue);
          targetLevel = nodeLevel >= 0 ? nodeLevel + 1 : 1;
        }
      }
      
      const nodeForChildren = {
        ...selectedNode,
        level: targetLevel,
        hierarchyType: hierarchyName
      };
      
      // Load children of selected node
      const data = await loadFilterData('productid', nodeForChildren, hierarchyName);
      
      if (data && data.children && data.children.length > 0) {
        // Store the node with its children for future use
        setAllLoadedNodes(prev => {
          const newMap = new Map(prev);
          newMap.set(selectedValue, {
            ...selectedNode,
            children: data.children
          });
          return newMap;
        });
        
        // Transform children to flat dropdown options (no tree structure)
        const glOptions = data.children.map((child: any) => ({
          value: child.value,
          label: child.title
        }));
        
        setDynamicOptions(prev => ({ 
          ...prev, 
          'documentSections_executive_pnl_pnl_by_gl': glOptions 
        }));
      } else {
        // No children found - show the parent node itself as option
        const glOptions = [{
          value: selectedNode.value,
          label: selectedNode.title
        }];
        
        setDynamicOptions(prev => ({ 
          ...prev, 
          'documentSections_executive_pnl_pnl_by_gl': glOptions 
        }));
      }
    } catch (error) {
      logger.error('Failed to load GL options for product line:', error as Error);
      // Set empty options on error
      setDynamicOptions(prev => ({ 
        ...prev, 
        'documentSections_executive_pnl_pnl_by_gl': [] 
      }));
    } finally {
      setIsGLOptionsLoading(false);
    }
  }, [loadFilterData, allLoadedNodes, mfrFormSchema.sections, dynamicOptions.product_lines]);

  // Helper method to update documentSections for dynamic dropdown changes
  const updateDocumentSectionsForDynamicDropdown = useCallback((
    parentId: 'pl-slice' | 'pl-drill-down',
    selectedOptions: string[],
    optionsArray: Array<{ value: string; label: string }>,
    setValue: any,
    getValues: any
  ) => {
    if (!setValue || !getValues) return;
    
    const currentDocumentSections = getValues().documentSections || {};
    const selectedIds = new Set(selectedOptions);
    const updatedDocumentSections = { ...currentDocumentSections };
    
    // Update parent entry to store filter values
    updatedDocumentSections[parentId] = { filterValue: selectedOptions };
    
    // Initialize new items with default checkbox states
    selectedOptions.forEach((optionValue: string) => {
      if (!updatedDocumentSections[optionValue]) {
        updatedDocumentSections[optionValue] = {
          includeTable: true,
          includeCommentary: true,
          scale: MILLIONS_VALUE
        };
      }
    });
    
    // Remove entries for options that are no longer selected
    optionsArray.forEach(opt => {
      if (!selectedIds.has(opt.value)) {
        delete updatedDocumentSections[opt.value];
      }
    });
    
    setValue('documentSections', updatedDocumentSections);
  }, []);

  // Helper method to create leaf sections for dynamic dropdown options
  const createLeafSectionsForOptions = useCallback((
    selectedOptions: string[],
    optionsArray: Array<{ value: string; label: string }>
  ) => {
    return selectedOptions.map((optionValue: string) => {
      return createLeafSection(optionValue, getLabelByValue(optionsArray, optionValue), { 
        includeTable: true,
        includeCommentary: true,
        scale: MILLIONS_VALUE
      });
    });
  }, []);

  // MFR-specific field change handler with cascading logic
  const handleFieldChange = useCallback(async (fieldName: string, value: any, setValue?: any, getValues?: any) => {
    logger.info(`MFR field "${fieldName}" changed:`, value);
    
    // Clear document section error when checkbox is checked
    if (fieldName === 'documentSections_clearError') {
      setDocumentSectionError('');
      return;
    }
    
    if (fieldName === `product_lines${FIELD_EVENTS.SEARCH}`) {
      const { searchValue, filterType } = value;
      await handleProductLineSearch(searchValue, filterType);
      return;
    }
    
    if (fieldName === `product_lines${FIELD_EVENTS.LOAD_DATA}`) {
      const { node, filterType } = value;
      await handleProductLineLoad(node, filterType);
      return;
    }
    
    // Handle P&L by Slice filter selection
    if (fieldName === 'documentSections_pl-slice_filter') {
      const selectedOptions = value || [];
      // Create leaf sections for all selected options
      const newChildren = createLeafSectionsForOptions(selectedOptions, PL_SLICE_OPTIONS);
      
      // Initialize and clean up documentSections
      updateDocumentSectionsForDynamicDropdown('pl-slice', selectedOptions, PL_SLICE_OPTIONS, setValue, getValues);
      
      // Update dynamic options to reflect the new children
      setDynamicOptions(prev => ({
        ...prev,
        'documentSections_pl-slice_children': newChildren
      }));
      return;
    }
    
    // Handle P&L Drill Down filter selection
    if (fieldName === 'documentSections_pl-drill-down_filter') {
      const selectedOptions = value || [];
      
      // Create leaf sections for all selected options (using label for display)
      const newChildren = createLeafSectionsForOptions(selectedOptions, PL_DRILL_DOWN_OPTIONS);
      
      // Initialize and clean up documentSections
      updateDocumentSectionsForDynamicDropdown('pl-drill-down', selectedOptions, PL_DRILL_DOWN_OPTIONS, setValue, getValues);
      
      // Update dynamic options to reflect the new children
      setDynamicOptions(prev => ({
        ...prev,
        'documentSections_pl-drill-down_children': newChildren
      }));
      return;
    }
    
    // Handle MFR-specific cascading logic
    if (fieldName === 'product_lines') {
      // Reset P&L by GL filter selections when product line changes
      // Clear the dynamic options first to reset the UI
      setDynamicOptions(prev => ({
        ...prev,
        'documentSections_executive_pnl_pnl_by_gl': [],
        'documentSections_executive_pnl_pnl_by_gl_reset': Date.now() // Trigger reset
      }));
      
      // Clear GL field selections when product line changes (whether cleared or changed to a new value)
      if (setValue && getValues) {
        const currentDocumentSections = getValues().documentSections || {};
        const updatedDocumentSections = { ...currentDocumentSections };
        
        // Clear filter value and optionally uncheck checkboxes for P&L by GL
        if (updatedDocumentSections['executive_pnl_pnl_by_gl']) {
          if (!value || value === '') {
            // Product line cleared - uncheck both checkboxes and clear filter
            updatedDocumentSections['executive_pnl_pnl_by_gl'] = {
              ...updatedDocumentSections['executive_pnl_pnl_by_gl'],
              includeTable: false,
              includeCommentary: false,
              filterValue: undefined
            };
          } else {
            // Product line changed to a different value - keep checkboxes but clear filter selections
            updatedDocumentSections['executive_pnl_pnl_by_gl'] = {
              ...updatedDocumentSections['executive_pnl_pnl_by_gl'],
              filterValue: undefined
            };
          }
          setValue('documentSections', updatedDocumentSections);
        }
      }
      
      // Load GL options from API based on selected product line (if not empty)
      if (value && value !== '') {
        await loadGLOptionsForProductLine(value);
      }
    }
    
    // Update comparison scenario options when period changes
    if (fieldName === 'period') {
      const comparisonOptions = getComparisonScenarioOptions(value);
      const hasPeriod = value && value !== '';
      setDynamicOptions(prev => ({ 
        ...prev, 
        'comparison_scenario': comparisonOptions,
        'comparison_scenario_disabled': !hasPeriod,
        'comparison_scenario_placeholder': hasPeriod ? 'Select comparison scenario...' : 'Select Period First'
      }));
      
      // Reset comparison scenario field when period changes
      if (setValue) {
        setValue('comparison_scenario', []);
      }
    }
    
    // Future MFR cascading dependencies can be added here, e.g.:
    // if (fieldName === 'marketplace') {
    //   const regionOptions = getRegionOptionsForMarketplace(value);
    //   setDynamicOptions(prev => ({ ...prev, 'region': regionOptions }));
    // }
  }, [handleProductLineSearch, handleProductLineLoad, loadGLOptionsForProductLine]);

  // Helper function to update tree node children
  const updateTreeNodeChildren = (treeData: any[], targetNode: any, children: any[]): any[] => {
    return treeData.map(node => {
      if (node.key === targetNode.key || node.value === targetNode.value) {
        return {
          ...node,
          children: children.map((child: any) => ({
            value: child.value || child.key,
            title: child.title || child.label || child.name,
            key: child.key || child.value,
            isLeaf: child.isLeaf !== false,
            hierarchyType: node.hierarchyType // Inherit hierarchy from parent
          }))
        };
      }
      if (node.children && node.children.length > 0) {
        return {
          ...node,
          children: updateTreeNodeChildren(node.children, targetNode, children)
        };
      }
      return node;
    });
  };

  // Get initial form data with default values
  const getInitialValues = () => {
    const savedData = getFormData(sessionFormId);

    // Apply default values from schema
    const defaultValues: any = {};
    mfrFormSchema.sections.forEach((section) => {
      section.fields.forEach((field) => {
        if (field.defaultValue !== undefined) {
          defaultValues[field.name] = field.defaultValue;
        }
        
        // Handle documentSectionsTable field type - initialize default values for table items
        if (field.type === 'documentSectionsTable') {
          const tableField = field as any;
          const tableData = tableField.tableData || [];
          const documentSectionsDefaults: any = {};
          
          // Helper function to traverse tree and initialize leaf nodes
          const initializeLeafNodes = (nodes: any[]) => {
            nodes.forEach((node: any) => {
              if (node.children && node.children.length > 0) {
                // This is a parent node, traverse its children
                initializeLeafNodes(node.children);
              } else {
                // This is a leaf node, initialize its defaults
                documentSectionsDefaults[node.id] = {
                  includeTable: node.includeTable ?? false,
                  includeCommentary: node.includeCommentary ?? false,
                  scale: node.scale ?? SCALE_CONFIG.DEFAULT
                };
              }
            });
          };
          
          // Initialize defaults for all leaf nodes in tree
          initializeLeafNodes(tableData);
          
          defaultValues[field.name] = documentSectionsDefaults;
        }
      });
    });

    return { 
      ...defaultValues, 
      ...savedData,
      // Only reset comparison_scenario for new workflows
      ...(isEditMode ? {} : { comparison_scenario: [] })
    };
  };

  // Show loading screen while fetching workflow data in edit mode
  if (isLoadingWorkflow) {
    return <LoadingScreen message="Loading workflow data..." />;
  }

  return (
    <ContentLayout
      defaultPadding
      header={
        <Header
          variant="h1"
          description={isEditMode 
            ? "Edit your MFR report configuration and document sections"
            : "Configure your MFR report with the required filters and document sections"
          }
        >
          {isEditMode ? 'Edit MFR Report' : 'Create MFR Report'}
        </Header>
      }
    >
      <SpaceBetween size="l">
        {/* The Configurable Form with custom action buttons */}
        <ConfigurableForm
          workflowId={sessionFormId}
          schema={mfrFormSchema}
          initialValues={getInitialValues()}
          onFieldChange={handleFieldChange}
          dynamicOptions={{
            ...dynamicOptions,
            product_lines_loadingNodes: loadingNodes,
            product_lines_searchLoading: isSearchLoading,
            'documentSections_executive_pnl_pnl_by_gl_loading': isGLOptionsLoading,
            'documentSections_error': documentSectionError
          } as any}
          showHeader={false}
          actionButtons={[
            { text: 'Cancel', variant: 'secondary', position: 'left', handler: handleCancel, bypassValidation: true },
            { text: isEditMode ? 'Update' : 'Save', variant: 'primary', position: 'right', handler: handleSave }
          ]}
        />
      </SpaceBetween>

      <ConfirmationModal
        visible={cancelModal}
        title="Confirm Cancellation"
        message="Are you sure you want to cancel? Any unsaved changes will be lost."
        confirmText="Yes, Cancel"
        cancelText="No, Keep Editing"
        variant="primary"
        onConfirm={handleCancelConfirm}
        onCancel={handleCancelModalClose}
      />
    </ContentLayout>
  );
};
